import api from "../../api";
import { getCookie, setCookie } from "../../mixins/cookies";
import { analytics } from "@/mixins/analytics";
import router from "../../router";
import features from "../../features.json";

function initialState() {
  return {
    schools: [],
    mySchools: [],
    schoolsLogo: {},
    schoolStatus: []
  };
}

const schoolsModule = {
  state: initialState,
  getters: {
    schools: state => state.schools,
    mySchools: state => state.mySchools,
    schoolsLogo: state => {
      return state.mySchools.map(school => ({
        schoolId: school.schoolId,
        logo: school.details.logo,
        name: school.details.name,
        colour: school.details.colour,
        schoolCode: school.schoolCode
      }));
    },
    schoolStatus: state => state.schoolStatus
  },
  mutations: {
    SET_SCHOOLS: function(state, schools) {
      state.schools = schools;
    },
    SET_MYSCHOOL: function(state, selectedSchool) {
      state.mySchools = [...state.mySchools, { ...selectedSchool }];
    },
    SET_SCHOOL_STATUS: function(state, schoolStatus) {
      state.schoolStatus = schoolStatus;
    },
    UPDATE_SCHOOLS: function(state, schools) {
      state.mySchools = schools;
    },
    ENABLE_SCHOOL_NOTIFICATIONS: function(state, { schoolId, type }) {
      if (type) {
        state.mySchools = state.mySchools.map(school =>
          school.schoolId === schoolId
            ? {
                ...school,
                pushNotifications: Object.assign({}, school.pushNotifications, {
                  [type]: true
                })
              }
            : school
        );
      }
      setCookie(`pushNotifications`, "true", 3650);

      const appBuild = getCookie("appbuild");
      if (parseInt(appBuild) >= features.nonMandatoryNotification) {
        //eslint-disable-next-line
        NativeJS.subscribeTopic(schoolId, type);
      } else {
        //eslint-disable-next-line
        NativeJS.subscribeTopic(schoolId);
      }
    },
    DISABLE_SCHOOL_NOTIFICATIONS: function(state, { schoolId, type }) {
      if (type) {
        state.mySchools = state.mySchools.map(school =>
          school.schoolId === schoolId
            ? {
                ...school,
                pushNotifications: Object.assign({}, school.pushNotifications, {
                  [type]: false
                })
              }
            : school
        );
      }

      const appBuild = getCookie("appbuild");
      if (parseInt(appBuild) >= features.nonMandatoryNotification) {
        //eslint-disable-next-line
        NativeJS.unsubscribeTopic(schoolId, type);
      } else {
        //eslint-disable-next-line
        NativeJS.unsubscribeTopic(schoolId);
      }
    },
    RESET_SCHOOLS: function(state) {
      const s = initialState();
      Object.keys(s).forEach(key => {
        state[key] = s[key];
      });
    },
    UPDATE_SCHOOL_DETAILS: function(state, details) {
      let school = state.mySchools.find(
        school => school.schoolCode === details.schoolCode
      );
      if (school) {
        school.details = details.data.details;
        school.features = details.data.features;
        school.links = details.data.links;
        school.tags = details.data.tags;
        school.essentialApps = details.data.essentialApps;
      }
    }
  },
  actions: {
    fetchSchools: function({ commit }) {
      if (!this.getters.isLoading) {
        commit("SET_IS_LOADING", true);
      }

      api.schools
        .fetchSchools()
        .then(response => {
          commit("SET_SCHOOLS", response);
        })
        .finally(() => {
          if (this.getters.isLoading) {
            commit("SET_IS_LOADING", false);
          }
        });
    },
    getSchoolStatus: function({ commit }) {
      if (!this.getters.isLoading) {
        commit("SET_IS_LOADING", true);
      }
      let appID = this.getters.appID;
      api.schools
        .getSchoolStatus(appID)
        .then(response => {
          commit("SET_SCHOOL_STATUS", response);
        })
        .finally(() => {
          if (this.getters.isLoading) {
            commit("SET_IS_LOADING", false);
          }
        });
    },
    selectSchool: function({ commit }, schoolCode) {
      return new Promise((resolve, reject) => {
        if (!this.getters.isLoading) {
          commit("SET_IS_LOADING", true);
        }
        api.schools
          .selectSchool(schoolCode)
          .then(response => {
            let newsletterFilter = this.getters.appFilters.newsletterFilter;
            let eventFilter = this.getters.appFilters.eventFilter;
            let newsFilter = this.getters.appFilters.newsFilter;
            let newsPersonalisation = this.getters.appFilters
              .newsPersonalisation;

            newsletterFilter.push({ id: response.schoolId, value: true });
            eventFilter.push({ id: response.schoolId, value: true });
            newsFilter.push({ id: response.schoolId, value: true });
            newsPersonalisation.push({
              id: response.schoolId,
              filters: [
                {
                  id: "Get all updates",
                  value: true
                },
                {
                  id: "Preschool",
                  value: true
                },
                {
                  id: "Kindergarten",
                  value: true
                },
                {
                  id: "Year 1",
                  value: true
                },
                {
                  id: "Year 2",
                  value: true
                },
                {
                  id: "Year 3",
                  value: true
                },
                {
                  id: "Year 4",
                  value: true
                },
                {
                  id: "Year 5",
                  value: true
                },
                {
                  id: "Year 6",
                  value: true
                },
                {
                  id: "Year 7",
                  value: true
                },
                {
                  id: "Year 8",
                  value: true
                },
                {
                  id: "Year 9",
                  value: true
                },
                {
                  id: "Year 10",
                  value: true
                },
                {
                  id: "Year 11",
                  value: true
                },
                {
                  id: "Year 12",
                  value: true
                }
              ]
            });

            analytics("added_school", {
              school_code: `S${response.schoolCode}`,
              school_name: response.details.name.replaceAll(" ", "-")
            });

            let pushNotificationsCookie = getCookie("pushNotifications");
            response.pushNotifications = {
              general: pushNotificationsCookie === "true"
            };
            if (
              parseInt(getCookie("appbuild")) >=
              features.nonMandatoryNotification
            ) {
              response.pushNotifications = Object.assign(
                response.pushNotifications,
                {
                  news: pushNotificationsCookie === "true",
                  event: pushNotificationsCookie === "true",
                  newsletter: pushNotificationsCookie === "true",
                  reminder: pushNotificationsCookie === "true"
                }
              );
            }

            let schoolData = response;

            let appID = this.getters.appID;
            this.dispatch("getNews", { page: 1, itemsPerPage: 10 });
            api.essentials
              .fetchEssentialApps(response.schoolCode)
              .then(response => {
                schoolData.essentialApps = response;
                commit("SET_MYSCHOOL", schoolData);
              })
              .catch(() => {
                commit("SET_MYSCHOOL", schoolData);
              });

            api.schools.addSchoolStatus(appID, response.schoolCode).then(() => {
              if (
                parseInt(getCookie("appbuild")) >=
                features.nonMandatoryNotification
              ) {
                ["news", "event", "newsletter", "reminder"].forEach(topic => {
                  if (response.pushNotifications[topic]) {
                    //eslint-disable-next-line
                    NativeJS.subscribeTopic(schoolData.schoolId, topic);
                  }
                });
              } else if (response.pushNotifications["general"]) {
                //eslint-disable-next-line
                NativeJS.subscribeTopic(schoolData.schoolId, "general");
              }
              this.dispatch("getSchoolStatus");
              resolve();
            });
          })
          .catch(err => {
            //eslint-disable-next-line
            NativeJS.debugLog(`Select school failed ${err.toString()}`);
            reject(err);
          })
          .finally(() => {
            if (this.getters.isLoading) {
              commit("SET_IS_LOADING", false);
              resolve();
            }
          });
      });
    },
    removeSchool: function({ commit, dispatch }, details) {
      let schools = this.state.schoolsModule.mySchools.filter(
        school => school.schoolId !== details.schoolId
      );
      commit("UPDATE_SCHOOLS", schools);

      // remove all of the schools filters
      dispatch("removeSchoolFromFilter", details.schoolId, { root: true });

      // remove push notifications for school
      dispatch("setSchoolPushNotifications", { schoolId: details.schoolId });

      let appID = this.getters.appID;

      // if there are schools left after removing one, redirect to the next school in the list
      // otherwise redirect to the school setup page
      if ((schools.length > 0) & details.redirectToNextSchool) {
        api.schools
          .removeSchoolStatus(appID, details.schoolCode, details.schoolId)
          .then(() => {
            this.dispatch("getSchoolStatus");
          });
        router.push(`/schools/${schools[0].schoolId}`);
      } else if ((schools.length > 0) & !details.redirectToNextSchool) {
        api.schools.removeSchoolStatus(
          appID,
          details.schoolCode,
          details.schoolId
        );
      } else {
        api.schools
          .removeSchoolStatus(appID, details.schoolCode, details.schoolId)
          .then(() => {
            console.log("school removed");
          });
        router.push("/school-setup");
      }
    },
    setSchoolPushNotifications: function({ commit }, details) {
      if (details.value) {
        commit("ENABLE_SCHOOL_NOTIFICATIONS", details);
        this.dispatch("setAppID");
        // this.dispatch('registerAllSchools');
        this.dispatch("registerApp");
      } else {
        commit("DISABLE_SCHOOL_NOTIFICATIONS", details);
      }
    },
    registerAllSchools: function({ dispatch }) {
      let appID = this.getters.appID;
      let schools = this.getters.mySchools;
      let pushNotificationsCookie = getCookie("pushNotifications");
      if (pushNotificationsCookie === "true") {
        api.schools.registerAllSchools(appID, schools);
      } else {
        schools.forEach(school => {
          dispatch("setSchoolPushNotifications", { schoolId: school.schoolId });
        });
      }
    },
    fetchSchoolData: function({ commit }) {
      let schools = this.getters.mySchools;
      schools.forEach(school => {
        api.schools.selectSchool(school.schoolCode).then(response => {
          let data = response;
          api.essentials
            .fetchEssentialApps(school.schoolCode)
            .then(response => {
              data.essentialApps = response;
              commit("UPDATE_SCHOOL_DETAILS", {
                schoolCode: school.schoolCode,
                data: data
              });
            })
            .catch(() => {
              commit("UPDATE_SCHOOL_DETAILS", {
                schoolCode: school.schoolCode,
                data: data
              });
            });
        });
      });
    }
  }
};

export default schoolsModule;
